import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, ProgressBar, TableSkeleton } from '../components/Feedback';
import { ConfidenceBadge, IntakeChip, ScenarioChip, StatusChip } from '../components/Chips';
import { SelectField, TextField, enumOptions, parseEnumValue } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { submissionBlockedReason } from '../components/Onboarding';
import { useAuth } from '../lib/auth';
import { documents } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useOnboarding } from '../lib/onboarding';
import { useToast } from '../lib/toast';
import type { DocumentSummaryDto } from '../lib/types';

type DocumentColumn = 'file' | 'scenario' | 'vendor' | 'invoice' | 'total' | 'confidence' | 'status' | 'uploaded';

function documentSortValue(doc: DocumentSummaryDto, key: DocumentColumn): string | number {
  switch (key) {
    case 'file':
      return doc.fileName;
    case 'scenario':
      return doc.scenario;
    case 'vendor':
      return doc.vendorName ?? '';
    case 'invoice':
      return doc.invoiceNumber ?? '';
    case 'total':
      return doc.total ?? 0;
    case 'confidence':
      return doc.confidence ?? 0;
    case 'status':
      return doc.status;
    case 'uploaded':
      return doc.createdUtc;
    default:
      return '';
  }
}
import {
  DocumentStatus,
  DocumentStatusLabels,
  GatewayScenario,
  GatewayScenarioDescriptions,
  GatewayScenarioLabels,
} from '../lib/enums';
import { errorMessage } from '../lib/api';
import { formatBytes, formatDateTime, formatMoney, formatRelative } from '../lib/format';

/** Mirrors GatewayOptions.MaxUploadMegabytes (default 25) so oversized files fail fast. */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

interface UploadItem {
  key: string;
  fileName: string;
  sizeBytes: number;
  progress: number;
  state: 'uploading' | 'done' | 'error';
  message?: string;
  documentId?: string;
  abort?: () => void;
}

export function DocumentsPage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const canUpload = can('documents.upload');

  // Shared onboarding cache (the Dashboard reads the same copy). While it loads, uploads stay
  // enabled — the API is the real gate, this only explains a refusal up front.
  const onboarding = useOnboarding();
  const reloadOnboarding = onboarding.reload;
  const blockedReason = onboarding.data ? submissionBlockedReason(onboarding.data) : null;
  const uploadsBlocked = blockedReason !== null;
  const canDrop = canUpload && !uploadsBlocked;

  const [scenario, setScenario] = useState<GatewayScenario>(GatewayScenario.NewBill);
  const [qboVendorId, setQboVendorId] = useState('');
  const [targetQboEntityId, setTargetQboEntityId] = useState('');
  const [notes, setNotes] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [statusFilter, setStatusFilter] = useState<DocumentStatus | null>(null);
  const [scenarioFilter, setScenarioFilter] = useState<GatewayScenario | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounced(search, 350);

  const list = useAsync(
    (signal) =>
      documents.list(
        { status: statusFilter, scenario: scenarioFilter, search: debouncedSearch, page, pageSize },
        signal,
      ),
    [statusFilter, scenarioFilter, debouncedSearch, page, pageSize],
  );

  const docTable = useSortableTable<DocumentColumn>({
    storageKey: 'documentsListColumnWidths',
    columns: {
      file: { minWidth: 180 },
      scenario: { minWidth: 110 },
      vendor: { minWidth: 140 },
      invoice: { minWidth: 120 },
      total: { minWidth: 90 },
      confidence: { minWidth: 90 },
      status: { minWidth: 120 },
      uploaded: { minWidth: 120 },
    },
  });

  const startUpload = useCallback(
    (file: File) => {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      if (file.size > MAX_UPLOAD_BYTES) {
        setUploads((current) => [
          ...current,
          {
            key,
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            state: 'error',
            message: `Too large — the gateway accepts files up to ${formatBytes(MAX_UPLOAD_BYTES)}.`,
          },
        ]);
        return;
      }

      const handle = documents.upload(
        file,
        {
          scenario,
          qboVendorId: qboVendorId.trim() || null,
          targetQboEntityId: targetQboEntityId.trim() || null,
          notes: notes.trim() || null,
        },
        (percent) => {
          setUploads((current) =>
            current.map((item) => (item.key === key ? { ...item, progress: percent } : item)),
          );
        },
      );

      setUploads((current) => [
        ...current,
        {
          key,
          fileName: file.name,
          sizeBytes: file.size,
          progress: 0,
          state: 'uploading',
          abort: handle.abort,
        },
      ]);

      handle.promise
        .then((summary) => {
          setUploads((current) =>
            current.map((item) =>
              item.key === key
                ? { ...item, progress: 100, state: 'done', documentId: summary?.id, message: summary?.statusMessage ?? undefined }
                : item,
            ),
          );
          toast.success('Upload accepted', `${file.name} is queued for extraction.`);
          list.reload();
          // The upload answered after iERP AI accepted the pages, and the gateway stored the
          // allowance iERP AI returned with that answer - re-read it (no iERP AI call) so the
          // remaining pages shown are the ones left after this document.
          reloadOnboarding();
        })
        .catch((error: unknown) => {
          const message = errorMessage(error);
          setUploads((current) =>
            current.map((item) => (item.key === key ? { ...item, state: 'error', message } : item)),
          );
          toast.error('Upload failed', `${file.name}: ${message}`);
        });
    },
    [scenario, qboVendorId, targetQboEntityId, notes, toast, list, reloadOnboarding],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      // Belt and braces: the controls are disabled, but a stray drop must not queue work either.
      if (uploadsBlocked) return;
      Array.from(fileList).forEach(startUpload);
    },
    [startUpload, uploadsBlocked],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (!canDrop) return;
    handleFiles(event.dataTransfer.files);
  };

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current += 1;
    if (canDrop) setDragging(true);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = canDrop ? 'copy' : 'none';
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const activeUploads = uploads.filter((item) => item.state === 'uploading').length;

  return (
    <>
      <PageHeader
        title="Documents"
        description="Drop vendor invoices and bills here. The AI bot extracts, the gateway posts to QuickBooks."
        actions={
          <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
            {list.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {canUpload ? (
        <Panel
          title="Upload"
          description="Pick the scenario first — it decides what the gateway does with the extraction."
        >
          {uploadsBlocked ? (
            <div
              className={`banner ${onboarding.data?.licence.quotaExhausted ? 'banner-info' : 'banner-warn'} banner-inline`}
              role="status"
            >
              <div className="banner-body">
                <p className="banner-title">Uploads are paused</p>
                <p className="banner-detail">{blockedReason}</p>
              </div>
              <Link className="btn btn-secondary btn-sm" to="/">
                Open the dashboard
              </Link>
            </div>
          ) : null}

          <div className="upload-grid">
            <div className="upload-config">
              <SelectField
                label="Scenario"
                value={String(scenario)}
                onChange={(value) => setScenario(parseEnumValue<GatewayScenario>(value) ?? GatewayScenario.NewBill)}
                options={enumOptions(GatewayScenarioLabels)}
                hint={GatewayScenarioDescriptions[scenario]}
              />

              {scenario === GatewayScenario.NewVendor ? (
                /*
                  ONE canonical vendor workflow, not two. Setting up a supplier needs a review step
                  this generic uploader does not have — the match against existing vendors, the
                  editable fields, and the approval that decides whether a vendor card is created at
                  all. Rather than quietly running a lesser version of that here, this hands over to
                  the workflow that does it properly.
                */
                <div className="notice notice-info">
                  <p>
                    Setting up a supplier has its own workflow, so you can check who they are before a
                    vendor card is created — and so iERP AI learns what their invoices look like.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate('/vendor-intake')}
                  >
                    Go to Vendor Intake
                  </button>
                </div>
              ) : null}

              <button
                type="button"
                className="btn btn-ghost btn-sm disclosure"
                aria-expanded={showOptions}
                onClick={() => setShowOptions((open) => !open)}
              >
                {showOptions ? '▾' : '▸'} Advanced upload options
              </button>

              {showOptions ? (
                <div className="upload-options">
                  <TextField
                    label="QuickBooks vendor id"
                    value={qboVendorId}
                    onChange={setQboVendorId}
                    placeholder="Optional — pins the vendor match"
                  />
                  <TextField
                    label="Target QuickBooks entity id"
                    value={targetQboEntityId}
                    onChange={setTargetQboEntityId}
                    placeholder="Optional — for existing bill / PO scenarios"
                  />
                  <TextField
                    label="Notes"
                    value={notes}
                    onChange={setNotes}
                    placeholder="Optional note stored with the document"
                  />
                </div>
              ) : null}
            </div>

            <div
              className={['dropzone', dragging ? 'dropzone-active' : '', uploadsBlocked ? 'dropzone-disabled' : '']
                .filter(Boolean)
                .join(' ')}
              aria-disabled={uploadsBlocked || undefined}
              onDrop={onDrop}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
            >
              <div className="dropzone-inner">
                <span className="dropzone-icon" aria-hidden="true">
                  {uploadsBlocked ? '⏸' : '⬇'}
                </span>
                <p className="dropzone-title">
                  {uploadsBlocked
                    ? 'Uploads unavailable'
                    : dragging
                      ? 'Release to upload'
                      : 'Drag invoices here'}
                </p>
                <p className="dropzone-sub">
                  {uploadsBlocked ? (
                    blockedReason
                  ) : (
                    <>
                      PDF, PNG, JPG or TIFF · up to {formatBytes(MAX_UPLOAD_BYTES)} each ·{' '}
                      {GatewayScenarioLabels[scenario]}
                    </>
                  )}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={uploadsBlocked}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="visually-hidden"
                  multiple
                  disabled={uploadsBlocked}
                  accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,image/*"
                  aria-label="Choose files to upload"
                  onChange={(event) => {
                    handleFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </div>
            </div>
          </div>

          {uploads.length > 0 ? (
            <div className="upload-list" aria-live="polite">
              <div className="upload-list-head">
                <h3 className="upload-list-title">
                  Uploads {activeUploads > 0 ? `(${activeUploads} in progress)` : ''}
                </h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setUploads((current) => current.filter((item) => item.state === 'uploading'))}
                >
                  Clear finished
                </button>
              </div>
              <ul className="upload-items">
                {uploads.map((item) => (
                  <li className={`upload-item upload-${item.state}`} key={item.key}>
                    <div className="upload-item-head">
                      <span className="upload-name">{item.fileName}</span>
                      <span className="upload-size">{formatBytes(item.sizeBytes)}</span>
                      {item.state === 'uploading' ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => item.abort?.()}
                        >
                          Cancel
                        </button>
                      ) : item.documentId ? (
                        <Link className="btn btn-ghost btn-sm" to={`/purchase-invoices?document=${item.documentId}`}>
                          Review
                        </Link>
                      ) : null}
                    </div>
                    <ProgressBar
                      value={item.state === 'done' ? 100 : item.progress}
                      tone={item.state === 'error' ? 'bad' : item.state === 'done' ? 'good' : 'info'}
                      label={`${item.fileName} upload progress`}
                    />
                    {item.message ? <p className="upload-message">{item.message}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Panel>
      ) : (
        <Panel title="Upload">
          <EmptyState
            title="Read-only access"
            description="Your role can review documents but not upload them."
            icon="🔒"
          />
        </Panel>
      )}

      <Panel title="All documents">
        <Toolbar>
          <TextField
            label="Search"
            type="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="File name, vendor or invoice number"
            className="toolbar-grow"
          />
          <SelectField
            label="Status"
            value={statusFilter === null ? '' : String(statusFilter)}
            onChange={(value) => {
              setStatusFilter(parseEnumValue<DocumentStatus>(value));
              setPage(1);
            }}
            options={enumOptions(DocumentStatusLabels)}
            placeholder="All statuses"
          />
          <SelectField
            label="Scenario"
            value={scenarioFilter === null ? '' : String(scenarioFilter)}
            onChange={(value) => {
              setScenarioFilter(parseEnumValue<GatewayScenario>(value));
              setPage(1);
            }}
            options={enumOptions(GatewayScenarioLabels)}
            placeholder="All scenarios"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={8} columns={7} />
        ) : list.error ? (
          <ErrorState message="Documents could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="No documents match"
            description="Adjust the filters, or drop a file above to get started."
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={docTable.columnStyle('file')} />
                <col style={docTable.columnStyle('scenario')} />
                <col style={docTable.columnStyle('vendor')} />
                <col style={docTable.columnStyle('invoice')} />
                <col style={docTable.columnStyle('total')} />
                <col style={docTable.columnStyle('confidence')} />
                <col style={docTable.columnStyle('status')} />
                <col style={docTable.columnStyle('uploaded')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="File"
                    sortKey="file"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('file')}
                    onResizeStart={docTable.beginResize('file')}
                  />
                  <SortableTh
                    label="Scenario"
                    sortKey="scenario"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('scenario')}
                    onResizeStart={docTable.beginResize('scenario')}
                  />
                  <SortableTh
                    label="Vendor"
                    sortKey="vendor"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('vendor')}
                    onResizeStart={docTable.beginResize('vendor')}
                  />
                  <SortableTh
                    label="Invoice"
                    sortKey="invoice"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('invoice')}
                    onResizeStart={docTable.beginResize('invoice')}
                  />
                  <SortableTh
                    label="Total"
                    sortKey="total"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    className="num"
                    style={docTable.columnStyle('total')}
                    onResizeStart={docTable.beginResize('total')}
                  />
                  <SortableTh
                    label="Confidence"
                    sortKey="confidence"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('confidence')}
                    onResizeStart={docTable.beginResize('confidence')}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('status')}
                    onResizeStart={docTable.beginResize('status')}
                  />
                  <SortableTh
                    label="Uploaded"
                    sortKey="uploaded"
                    currentSortKey={docTable.sortKey}
                    sortDir={docTable.sortDir}
                    onSort={docTable.toggleSort}
                    style={docTable.columnStyle('uploaded')}
                    onResizeStart={docTable.beginResize('uploaded')}
                  />
                </tr>
              </thead>
              <tbody>
                {docTable.sort(list.data.items, documentSortValue).map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <Link className="cell-link" to={`/purchase-invoices?document=${doc.id}`}>
                        {doc.fileName}
                      </Link>
                      <span className="cell-sub">
                        {formatBytes(doc.fileSizeBytes)}
                        {doc.pageCount !== null ? ` · ${doc.pageCount} pages` : ''} ·{' '}
                        <IntakeChip channel={doc.intakeChannel} />
                      </span>
                    </td>
                    <td>
                      <ScenarioChip scenario={doc.scenario} />
                    </td>
                    <td>
                      {doc.vendorName ?? '—'}
                      {doc.requiresVendorCreation ? <span className="cell-flag">new vendor</span> : null}
                    </td>
                    <td>
                      {doc.invoiceNumber ?? '—'}
                      <span className="cell-sub">{formatDateTime(doc.invoiceDate, '')}</span>
                    </td>
                    <td className="num">{formatMoney(doc.total, doc.currencyCode)}</td>
                    <td>
                      <ConfidenceBadge value={doc.confidence} size="sm" />
                    </td>
                    <td>
                      <StatusChip status={doc.status} />
                      {doc.hasValidationIssues ? <span className="cell-flag warn">issues</span> : null}
                      {doc.hasPendingApproval ? <span className="cell-flag">approval</span> : null}
                    </td>
                    <td>
                      {formatRelative(doc.createdUtc)}
                      <span className="cell-sub">{doc.uploadedByEmail ?? '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
